import { changePackageFlowRoute } from '@/features/dashboard/bookingChanges/routes/changePackageFlowRoute.ts';
import { ChangePackageStep } from '@/features/dashboard/bookingChanges/routes/changePackageSearch.ts';
import { ChangeRoute } from '@/features/dashboard/bookingChanges/types/ChangePackageFlowViewModel.ts';
import useChangePackageSummary from '@/features/dashboard/bookingChanges/hooks/useChangePackageSummary.ts';
import useChangePackageSettlement from '@/features/dashboard/bookingChanges/hooks/useChangePackageSettlement.ts';
import useClientAcceptanceView from '@/features/dashboard/bookingChanges/hooks/useClientAcceptanceView.ts';
import useResolveDraft from '@/features/dashboard/bookingChanges/hooks/useResolveDraft.ts';
import { DetailsMode } from '@/features/dashboard/bookingChanges/routes/detailsSearch.ts';
import useChangePackageFlowSelection from '@/features/dashboard/bookingChanges/hooks/useChangePackageFlowSelection.ts';
import useChangePackageFlowNavigation from '@/features/dashboard/bookingChanges/hooks/useChangePackageFlowNavigation.ts';
import { toChangePackageResolveRequest } from '@/features/dashboard/bookingChanges/components/flow/changePackageFlowResolveRequest.ts';
import { resendableChangeId } from '@/features/dashboard/bookingChanges/components/flow/changePackageSteps.ts';

/**
 * Cała obsługa przepływu W1 → W2 → W3: dane widocznego kroku, wybory operatora
 * i wprowadzenie paczki.
 *
 * Każdy krok czyta co innego, więc zapytania są wpięte warunkowo — niewidoczny
 * krok nie pyta o nic. Stan wyboru z kroku W2 (rozkład dopłaty, trasa,
 * dokumenty) stoi tutaj, bo to jedyne miejsce, które zna go w komplecie
 * w chwili wysyłania `resolve`.
 */
const useChangePackageFlow = () => {
  const { bookingId, changeId } = changePackageFlowRoute.useParams();
  const { step } = changePackageFlowRoute.useSearch();

  const summaryQuery = useChangePackageSummary(
    step === ChangePackageStep.SUMMARY ? bookingId : undefined,
  );
  const settlementQuery = useChangePackageSettlement(
    step === ChangePackageStep.SETTLEMENT ? bookingId : undefined,
    step === ChangePackageStep.SETTLEMENT ? changeId : undefined,
  );
  const acceptanceQuery = useClientAcceptanceView(
    step === ChangePackageStep.ACCEPTANCE ? bookingId : undefined,
  );

  const resolveDraft = useResolveDraft(bookingId);

  const selection = useChangePackageFlowSelection(
    settlementQuery.settlement?.documents ?? [],
    settlementQuery.settlement?.correctionTerms ?? null,
  );
  const { goToStep, goToBooking } = useChangePackageFlowNavigation(
    bookingId,
    changeId,
  );

  const submit = async () => {
    const consents = settlementQuery.consents;
    if (!consents) {
      return;
    }

    try {
      await resolveDraft.mutateAsync({
        changeId,
        request: toChangePackageResolveRequest({
          consents,
          route: selection.changeRoute,
          selectedDocumentIds: selection.selectedDocumentIds,
          correctionDueDate: selection.correctionDueDate,
          correctionPaymentMethods: selection.correctionPaymentMethods,
        }),
      });
    } catch {
      // Komunikat pokazuje już `useResolveDraft`; operator zostaje na kroku W2
      // z nietkniętym wyborem, żeby mógł poprawić i spróbować ponownie.
      return;
    }

    if (selection.changeRoute === ChangeRoute.CLIENT_ACCEPTANCE) {
      await goToStep(ChangePackageStep.ACCEPTANCE);
      return;
    }
    await goToBooking(DetailsMode.VIEW);
  };

  return {
    step,
    summaryQuery,
    settlementQuery,
    acceptanceQuery,
    resendChangeId: resendableChangeId(changeId, acceptanceQuery.change),
    selection,
    goToStep,
    goToBooking,
    submit,
    isSubmitting: resolveDraft.isPending,
  };
};

export default useChangePackageFlow;
